import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { apiGet, apiPost, registerForceLogout, withRetry, HttpError } from '../lib/api'
import { setItem, getItem, removeItem } from '../lib/storage'
import { STORAGE_KEYS } from '../lib/constants'
import { queryClient } from '../lib/queryClient'
import { addBreadcrumb, reportError } from '../lib/reporting'
import type { TokenResponse, UserResponse, LoginRequest, RegisterRequest, RegisterResponse } from '@warcarp/api-types'

interface AuthState {
  user: UserResponse | null
  isLoading: boolean
  isAuthenticated: boolean
  isExplicitLogout: boolean
}

interface AuthActions {
  login: (data: LoginRequest) => Promise<void>
  register: (data: RegisterRequest) => Promise<RegisterResponse>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  /** Called by screens that already hold a TokenResponse and want to
   *  hydrate the auth context without a second network round-trip. */
  setSession: (tokens: TokenResponse) => Promise<void>
}

export const AuthContext = createContext<(AuthState & AuthActions) | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isExplicitLogout, setIsExplicitLogout] = useState(false)

  // persistUser is used by loadUser, login, and setSession. Stable reference
  // (useCallback with no deps) so the functions that call it don't need to
  // list it in their own dependency arrays.
  const persistUser = useCallback(async (u: UserResponse) => {
    try {
      await setItem(STORAGE_KEYS.USER, JSON.stringify(u))
    } catch {
      // Non-fatal: caching is a nicety, not required for auth to work.
    }
  }, [])

  // Stable logout — registered with the global force-logout handler so the
  // 401-refresh path in api.ts can sign the user out without importing
  // AuthProvider directly.
  const logout = useCallback(async () => {
    setIsExplicitLogout(true)
    const refreshToken = await getItem(STORAGE_KEYS.REFRESH_TOKEN)
    if (refreshToken) {
      apiPost('/api/v1/auth/logout', { refreshToken }).catch((err: unknown) => {
        // Fire-and-forget: network failures during logout are non-fatal but
        // should leave a trace so we know if the revoke endpoint is flapping.
        addBreadcrumb({ category: 'auth', message: 'logout call failed', level: 'warning' })
        reportError(err instanceof Error ? err : new Error(String(err)), {
          tags: { area: 'auth', action: 'logout' },
        })
      })
    }
    await removeItem(STORAGE_KEYS.ACCESS_TOKEN)
    await removeItem(STORAGE_KEYS.REFRESH_TOKEN)
    await removeItem(STORAGE_KEYS.USER)
    // Wipe cached data on sign-out so the next account on this device can't
    // momentarily see this user's games.
    queryClient.clear()
    setUser(null)
  }, [])

  const login = useCallback(async (data: LoginRequest) => {
    const res = await apiPost<TokenResponse>('/api/v1/auth/login', data)
    setIsExplicitLogout(false)
    await setItem(STORAGE_KEYS.ACCESS_TOKEN, res.accessToken)
    await setItem(STORAGE_KEYS.REFRESH_TOKEN, res.refreshToken)
    await persistUser(res.user)
    // Drop any cached queries from a previous session on THIS device. Without
    // this, a freshly-signed-in account briefly renders the prior user's games
    // (stale ['games'] cache) before the refetch returns — the lobby "results
    // flash then go blank" flicker.
    queryClient.clear()
    setUser(res.user)
  }, [persistUser])

  const register = useCallback(async (data: RegisterRequest): Promise<RegisterResponse> => {
    const res = await apiPost<RegisterResponse>('/api/v1/auth/register', data)
    // Registration no longer issues tokens — the user must confirm their email
    // before they can log in. Callers are responsible for navigating to the
    // check-email screen.
    return res
  }, [])

  const setSession = useCallback(async (tokens: TokenResponse) => {
    setIsExplicitLogout(false)
    await setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken)
    await setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken)
    await persistUser(tokens.user)
    // Same reason as login(): start the new session with a clean cache.
    queryClient.clear()
    setUser(tokens.user)
  }, [persistUser])

  const refreshUser = useCallback(async () => {
    const me = await apiGet<UserResponse>('/api/v1/auth/me')
    await persistUser(me)
    setUser(me)
  }, [persistUser])

  useEffect(() => {
    registerForceLogout(logout)

    async function loadUser() {
      const token = await getItem(STORAGE_KEYS.ACCESS_TOKEN)
      if (!token) {
        setIsLoading(false)
        return
      }

      // Optimistic hydration: show the authed UI IMMEDIATELY from the cached
      // user, so a cold start (and especially an OFFLINE launch) never flashes
      // the login screen while /auth/me is in flight. We then validate in the
      // background below.
      let hydrated = false
      try {
        const cached = await getItem(STORAGE_KEYS.USER)
        if (cached) {
          setUser(JSON.parse(cached) as UserResponse)
          hydrated = true
        }
      } catch {
        // Corrupt cache — ignore and fall back to the network result.
      }
      // If we couldn't hydrate optimistically we still want a spinner, not a
      // login flash, until the network call settles.
      if (hydrated) setIsLoading(false)

      try {
        // Ride out transient blips: withRetry retries network errors and 5xx,
        // but NOT a 401 (a deterministic HttpError) — an expired access token is
        // already refreshed inside apiFetch, so a 401 here means the refresh
        // genuinely failed and the session is dead.
        const me = await withRetry(
          () => apiGet<UserResponse>('/api/v1/auth/me'),
          3,
          [1_000, 2_000],
        )
        await persistUser(me)
        setUser(me)
      } catch (err) {
        // ONLY a genuine auth failure ends the session. A transient/network
        // failure must NOT log the user out — keep the cached user (or, if we
        // never hydrated, leave the tokens in place so a later request recovers).
        if (err instanceof HttpError && (err.status === 401 || err.status === 403)) {
          await removeItem(STORAGE_KEYS.USER)
          setUser(null)
        }
        // else: offline / 5xx — stay on the cached authed session.
      } finally {
        setIsLoading(false)
      }
    }

    void loadUser()
    // logout / persistUser are stable useCallback references — no need to list
    // them. loadUser is defined inside the effect and never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Memoize the context value so non-auth-state re-renders (e.g. a parent
  // component updating unrelated state) don't create a new object reference
  // and churn every consumer that subscribes to AuthContext.
  const value = useMemo(
    () => ({ user, isLoading, isAuthenticated: !!user, isExplicitLogout, login, register, logout, refreshUser, setSession }),
    [user, isLoading, isExplicitLogout, login, register, logout, refreshUser, setSession],
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
